import { z } from 'zod';

// ── Shared field rules ────────────────────────────────────────────────────────

/**
 * Validates the raw value that lives in the PhoneField input (after the +91 prefix).
 *
 * The input only holds what the user typed in the text box:
 *   - Indian numbers: 10 digits, spaces allowed (e.g. "98765 43210")
 *   - Foreign numbers (user bypasses prefix): starts with +, full E.164
 *
 * normalizePhone() converts either form to full E.164 on submit.
 */
export const phoneField = z
  .string()
  .min(1, 'Mobile number is required')
  .refine(
    (val) => {
      const clean = val.replace(/\s/g, '');
      const isIndian  = /^\d{10}$/.test(clean);
      const isE164    = /^\+[1-9]\d{9,14}$/.test(clean);
      return isIndian || isE164;
    },
    'Enter a valid 10-digit mobile number',
  );

export const otpField = z
  .string()
  .length(6, 'Enter the complete 6-digit code');

// ── Login schemas ─────────────────────────────────────────────────────────────

export const loginPhoneSchema   = z.object({ mobile_e164: phoneField });
export type  LoginPhoneValues   = z.infer<typeof loginPhoneSchema>;

export const loginOtpSchema     = z.object({ otp: otpField });
export type  LoginOtpValues     = z.infer<typeof loginOtpSchema>;

// ── Signup schemas ────────────────────────────────────────────────────────────

export const signupDetailsSchema = z.object({
  full_name: z
    .string()
    .min(2, 'Full name must be at least 2 characters')
    .max(100, 'Full name is too long')
    .regex(/^[a-zA-Z\s'-]+$/, 'Only letters, spaces, hyphens, and apostrophes'),
  mobile_e164: phoneField,
  email: z
    .string()
    .email('Enter a valid email address')
    .optional()
    .or(z.literal('')),
  // Optional — a code another customer shared. Kept lenient client-side
  // (the backend is the source of truth for validity); normalised to
  // uppercase on submit.
  referral_code: z
    .string()
    .max(20, 'Referral code is too long')
    .optional()
    .or(z.literal('')),
});
export type SignupDetailsValues = z.infer<typeof signupDetailsSchema>;

export const signupOtpSchema    = z.object({ otp: otpField });
export type  SignupOtpValues    = z.infer<typeof signupOtpSchema>;
